import { memo, useMemo } from 'react';
import { useTheme, Paper, Typography, Box } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

const AXIS_FONT_SIZE = 12;
const LINE_WEIGHT = 2;

// ─── Типы ────────────────────────────────────────────────────────────────────

export interface TelemetryPoint {
    time: string;
    value: number;
}

export interface DeviceTelemetryChartProps {
    points: TelemetryPoint[]; //Должны быть отсортированы по времени ASC
    label: string;
    unit: string;
    color?: string;
    height?: number;
    maxValue?: number;
    minValue?: number;
}

// ─── Внутренний тип ───────────────────────────────────────────────────────────

interface ChartEntry {
    time: string;
    ts: number;
    value: number | null;
}

// ─── UTC → локальное время ────────────────────────────────────────────────────

function utcToLocal(utcStr: string): string {
    const d = new Date(utcStr.replace(' ', 'T') + 'Z');
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ─── Подсказка ───────────────────────────────────────────────────────────────

interface TelemetryTooltipProps {
    active?: boolean;
    payload?: readonly unknown[];
    label?: number;
    labelText: string;
    unit: string;
    chartData: ChartEntry[];
}

function TelemetryTooltip({ active, label, labelText, unit, chartData }: TelemetryTooltipProps) {
    const theme = useTheme();
    if (!active || label == null) return null;

    const entry = chartData.find((d) => d.ts === label);
    if (!entry || entry.value === null) return null;

    return (
        <Paper
            elevation={3}
            sx={{
                px: 1.5,
                py: 1,
                border: `1px solid ${theme.palette.primary.dark}`,
                pointerEvents: 'none',
            }}
        >
            <Typography variant="caption" display="block" color="text.secondary">
                {entry.time}
            </Typography>
            <Typography variant="caption" display="block" color="text.primary">
                {labelText}: {entry.value} {unit}
            </Typography>
        </Paper>
    );
}

// ─── Компонент ───────────────────────────────────────────────────────────────

export default memo(function DeviceTelemetryChart({
    points,
    label,
    unit,
    color,
    height = 250,
    maxValue,
    minValue,
}: DeviceTelemetryChartProps) {
    const theme = useTheme();
    const lineColor = color ?? theme.palette.primary.main;
    const gridColor = theme.palette.divider;
    const textColor = theme.palette.text.secondary;

    // Данные для Recharts (сортируем по времени ASC)
    const chartData = useMemo<ChartEntry[]>(
        () =>
            [...points]
                .sort((a, b) => a.time.localeCompare(b.time))
                .map((p) => ({
                    time: utcToLocal(p.time),
                    ts: new Date(p.time.replace(' ', 'T') + 'Z').getTime(),
                    value: p.value,
                })),
        [points],
    );

    // Форматер оси X
    const xAxisConfig = useMemo(() => {
        const pad2 = (n: number) => String(n).padStart(2, '0');

        if (chartData.length < 2) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
                },
                label: 'время',
                ticks: undefined as number[] | undefined,
            };
        }

        const start = chartData[0].ts;
        const end = chartData[chartData.length - 1].ts;
        const spanMs = end - start;
        const DAY = 86_400_000;

        const TICK_COUNT = Math.min(chartData.length, 10);
        const step = spanMs / (TICK_COUNT - 1);
        const ticks = Array.from({ length: TICK_COUNT }, (_, i) => Math.round(start + i * step));

        if (spanMs < 2 * DAY) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
                },
                label: 'время',
                ticks,
            };
        }
        if (spanMs < 60 * DAY) {
            return {
                formatter: (v: number) => {
                    const d = new Date(v);
                    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
                },
                label: 'дата',
                ticks,
            };
        }
        const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
        return {
            formatter: (v: number) => {
                const d = new Date(v);
                return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
            },
            label: 'месяц',
            ticks,
        };
    }, [chartData]);

    const yDomain = useMemo<[number, number]>(() => {
        const values = chartData.filter((d) => d.value !== null).map((d) => d.value as number);
        if (!values.length) return [-1, 1];
        const lo = minValue ?? Math.min(...values);
        const hi = maxValue ?? Math.max(...values);
        if (lo === hi) return [lo - 1, hi + 1];
        return [lo, hi];
    }, [chartData, minValue, maxValue]);

    if (points.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                overflow: 'hidden',
                '& *:focus': { outline: 'none' },
            }}
        >
            <Typography variant="subtitle2" gutterBottom sx={{ px: 0.5 }}>
                {label}
            </Typography>
            <Box sx={{ width: '100%', height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

                        <XAxis
                            dataKey="ts"
                            type="number"
                            scale="time"
                            domain={['dataMin', 'dataMax']}
                            ticks={xAxisConfig.ticks}
                            orientation="top"
                            tickFormatter={xAxisConfig.formatter}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            label={{
                                value: xAxisConfig.label,
                                position: 'insideBottomRight',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dy: 2,
                            }}
                        />

                        <YAxis
                            type="number"
                            domain={yDomain}
                            tick={{ fill: textColor, fontSize: AXIS_FONT_SIZE }}
                            tickLine={{ stroke: gridColor }}
                            axisLine={{ stroke: gridColor }}
                            width={42}
                            tickFormatter={(v: number) => `${v}`}
                            label={{
                                value: unit,
                                position: 'insideTopLeft',
                                fill: textColor,
                                fontSize: AXIS_FONT_SIZE,
                                dx: 4,
                                dy: 12,
                            }}
                        />

                        <ReferenceLine y={0} stroke={gridColor} strokeWidth={1.2} />

                        <Tooltip
                            content={(props) => (
                                <TelemetryTooltip
                                    active={props.active}
                                    payload={props.payload}
                                    label={props.label as number | undefined}
                                    labelText={label}
                                    unit={unit}
                                    chartData={chartData}
                                />
                            )}
                            cursor={{
                                stroke: theme.palette.primary.light,
                                strokeWidth: 1.5,
                                strokeDasharray: '6 4',
                            }}
                            wrapperStyle={{ transition: 'opacity 150ms ease' }}
                        />

                        <Line
                            type="linear"
                            dataKey="value"
                            stroke={lineColor}
                            strokeWidth={LINE_WEIGHT}
                            isAnimationActive={false}
                            dot={
                                chartData.length <= 50
                                    ? {
                                          r: 3,
                                          stroke: lineColor,
                                          strokeWidth: 1.5,
                                          fill: theme.palette.background.paper,
                                      }
                                    : false
                            }
                            activeDot={{ r: 4, stroke: lineColor, strokeWidth: 2, fill: lineColor }}
                            connectNulls={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
});
